import React, { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  CreditCard, Download, Loader2, TrendingUp, TrendingDown,
  Wallet, ArrowUpRight, ArrowDownRight, FileText, Leaf, DollarSign, ChevronRight, Receipt
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('shrt_token');

export default function StripeFinancePage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [exporting, setExporting] = useState(null);
  const [logExport, setLogExport] = useState(true);
  const [exportLogs, setExportLogs] = useState([]);
  const [tab, setTab] = useState('charges');

  useEffect(() => { fetchOverview(); fetchExportLogs(); }, []);

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/payments/stripe/overview`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Laden');
    } finally { setLoading(false); }
  };

  const fetchExportLogs = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/payments/stripe/export-log`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setExportLogs(res.data.logs || []);
    } catch { /* ignore */ }
  };

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      params.append('include_fees', 'true');
      params.append('log', logExport ? 'true' : 'false');

      const res = await axios.get(
        `${API_URL}/api/payments/stripe/export/${format}?${params.toString()}`,
        { headers: { Authorization: `Bearer ${getToken()}` }, responseType: 'blob' }
      );

      const extMap = { camt054: 'xml', csv: 'csv', zip: 'zip', 'zip-by-account': 'zip', invoices: 'zip' };
      const ext = extMap[format] || 'bin';
      const filename = res.headers['content-disposition']?.match(/filename="(.+)"/)?.[1] || `stripe_export.${ext}`;
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      const labels = { zip: 'Gesamt-Export', 'zip-by-account': 'Pro Gegenkonto', camt054: 'camt.053 XML', csv: 'CSV', invoices: 'Rechnungen' };
      toast.success(`${labels[format] || format} exportiert`);
      if (logExport) fetchExportLogs();
    } catch (e) {
      if (e.response?.status === 404) toast.error('Keine Transaktionen im gewählten Zeitraum');
      else toast.error('Export fehlgeschlagen');
    } finally { setExporting(null); }
  };

  const fmtCHF = (v) => `CHF ${(v || 0).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('de-CH') : '-';
  const fmtDateTime = (d) => d ? new Date(d).toLocaleString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="animate-spin text-primary" size={32} />
        </div>
      </Layout>
    );
  }

  const { balance, summary, charges, transactions, payouts } = data || {};

  return (
    <Layout>
      <div className="space-y-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-['Oswald'] text-2xl sm:text-3xl font-bold uppercase tracking-wide" data-testid="finance-title">
              Stripe Finanzen
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Transaktionen, Gebühren, Auszahlungen</p>
          </div>
        </div>

        {/* Balance Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-card border border-border p-4" data-testid="card-available">
            <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider mb-2">
              <Wallet size={14} /> Verfügbar
            </div>
            <p className="text-xl font-bold text-green-500">{fmtCHF(balance?.available?.[0]?.amount)}</p>
          </div>
          <div className="bg-card border border-border p-4" data-testid="card-pending">
            <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider mb-2">
              <TrendingUp size={14} /> Ausstehend
            </div>
            <p className="text-xl font-bold text-amber-500">{fmtCHF(balance?.pending?.[0]?.amount)}</p>
          </div>
          <div className="bg-card border border-border p-4" data-testid="card-fees">
            <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider mb-2">
              <DollarSign size={14} /> Gebühren
            </div>
            <p className="text-xl font-bold text-red-400">{fmtCHF(summary?.total_fees)}</p>
          </div>
          <div className="bg-card border border-border p-4" data-testid="card-climate">
            <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider mb-2">
              <Leaf size={14} /> Climate
            </div>
            <p className="text-xl font-bold text-emerald-500">{fmtCHF(summary?.total_climate)}</p>
          </div>
        </div>

        {/* Export Section */}
        <div className="bg-card border border-border p-4 sm:p-5">
          <h2 className="font-['Oswald'] text-sm font-bold uppercase tracking-wide mb-3">Export für ClubDesk</h2>
          <div className="flex flex-col sm:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="block text-xs text-muted-foreground uppercase tracking-wider mb-1">Von</label>
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="rounded-none" data-testid="export-date-from" />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-muted-foreground uppercase tracking-wider mb-1">Bis</label>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="rounded-none" data-testid="export-date-to" />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={() => handleExport('zip')} disabled={!!exporting} className="rounded-none bg-primary hover:bg-red-700 text-white" data-testid="export-zip-btn">
                {exporting === 'zip' ? <Loader2 size={16} className="animate-spin mr-2" /> : <Download size={16} className="mr-2" />}
                Gesamt-Export
              </Button>
              <Button onClick={() => handleExport('zip-by-account')} disabled={!!exporting} variant="outline" className="rounded-none" data-testid="export-by-account-btn">
                {exporting === 'zip-by-account' ? <Loader2 size={16} className="animate-spin mr-2" /> : <Download size={16} className="mr-2" />}
                Pro Gegenkonto
              </Button>
              <Button onClick={() => handleExport('invoices')} disabled={!!exporting} variant="outline" className="rounded-none" data-testid="export-invoices-btn">
                {exporting === 'invoices' ? <Loader2 size={16} className="animate-spin mr-2" /> : <Receipt size={16} className="mr-2" />}
                Rechnungen
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Gesamt: CSV + camt.053 in einer ZIP &nbsp;|&nbsp; Pro Gegenkonto: Separate Dateien pro Konto &nbsp;|&nbsp; Rechnungen: Alle Rechnungen aus DB (bezahlt + offen)
          </p>
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none mt-2" data-testid="log-export-checkbox">
            <input type="checkbox" checked={logExport} onChange={e => setLogExport(e.target.checked)} className="w-3.5 h-3.5" />
            Export protokollieren
          </label>

          {/* Import-Anleitung */}
          <details className="mt-4 border-t border-border pt-3 group">
            <summary className="text-xs font-bold uppercase tracking-wider text-muted-foreground cursor-pointer select-none flex items-center gap-1">
              <ChevronRight size={14} className="transition-transform group-open:rotate-90" />
              Anleitung: Import in ClubDesk (camt.053)
            </summary>
            <div className="mt-3 text-xs text-muted-foreground space-y-2 pl-5">
              <p><strong>Voraussetzung:</strong> In ClubDesk unter <em>Rechnungen → Zahlungsverbindungen</em> muss mindestens eine Verbindung mit Buchungskonto hinterlegt sein (z.B. "Bank-Konto" → Konto 1020).</p>
              <ol className="list-decimal pl-4 space-y-1.5">
                <li><strong>Export herunterladen</strong> — "Gesamt-Export" oder "Pro Gegenkonto" klicken. Es wird eine ZIP-Datei mit CSV- und camt.053-Dateien heruntergeladen.</li>
                <li><strong>ZIP entpacken</strong> — Die gewünschte <code>.xml</code>-Datei (camt.053) auswählen.</li>
                <li><strong>ClubDesk → Buchhaltung → Import</strong> — "camt.053/054-Datei einlesen" wählen.</li>
                <li><strong>Zahlungsverbindung wählen</strong> — Die passende Verbindung auswählen (z.B. "Bank-Konto").</li>
                <li><strong>Gegenkonto zuweisen</strong> — Beim ersten Import: Gleichartige Zeilen markieren und Gegenkonto wählen (z.B. alle "Mitgliederbeitrag" → Konto 3000). ClubDesk merkt sich die Zuordnung für zukünftige Imports.</li>
                <li><strong>Import bestätigen</strong> — ClubDesk erstellt die Buchungen automatisch.</li>
              </ol>
              <p className="mt-2"><strong>Tipp:</strong> Mit "Pro Gegenkonto" erhältst du separate Dateien pro Buchungskonto. So musst du pro Datei nur einmal das Gegenkonto wählen.</p>
            </div>
          </details>

          {/* Export Protocol */}
          {exportLogs.length > 0 && (
            <div className="mt-4 border-t border-border pt-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Exportprotokoll</h3>
                <button
                  onClick={async () => {
                    if (!window.confirm('Exportprotokoll wirklich zurücksetzen?')) return;
                    try {
                      await axios.delete(`${API_URL}/api/payments/stripe/export-log`, { headers: { Authorization: `Bearer ${getToken()}` } });
                      setExportLogs([]);
                      toast.success('Exportprotokoll zurückgesetzt');
                    } catch { toast.error('Fehler beim Zurücksetzen'); }
                  }}
                  className="text-xs text-muted-foreground hover:text-foreground underline"
                  data-testid="clear-export-log-btn"
                >
                  Zurücksetzen
                </button>
              </div>
              <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                {exportLogs.map((log, i) => (
                  <div key={log.id || i} className="flex items-start gap-3 text-xs py-1.5 border-b border-border/50 last:border-0" data-testid={`export-log-${i}`}>
                    <span className="text-muted-foreground whitespace-nowrap min-w-[120px]">
                      {new Date(log.exported_at).toLocaleString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                    <span className="font-medium">{log.export_type}</span>
                    <span className="text-muted-foreground whitespace-nowrap">
                      {log.data_range_from && log.data_range_to
                        ? `${log.data_range_from} – ${log.data_range_to}`
                        : log.data_range_from || log.data_range_to || 'Alle Daten'}
                    </span>
                    {log.num_entries > 0 && <span className="text-muted-foreground">{log.num_entries} Eintr.</span>}
                    <span className="text-muted-foreground ml-auto">{log.exported_by}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-0 border-b border-border">
          {[
            { key: 'charges', label: 'Zahlungen', count: charges?.length },
            { key: 'payouts', label: 'Auszahlungen', count: payouts?.length },
            { key: 'all', label: 'Alle Bewegungen', count: transactions?.length },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${tab === t.key ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
              data-testid={`tab-${t.key}`}
            >
              {t.label} {t.count != null && <span className="text-xs opacity-60">({t.count})</span>}
            </button>
          ))}
        </div>

        {/* Charges Tab */}
        {tab === 'charges' && (
          <div className="bg-card border border-border overflow-x-auto">
            <table className="w-full text-sm" data-testid="charges-table">
              <thead>
                <tr className="border-b border-border text-xs text-muted-foreground uppercase tracking-wider">
                  <th className="text-left p-3">Datum</th>
                  <th className="text-left p-3">Name</th>
                  <th className="text-left p-3 hidden sm:table-cell">Rechnung</th>
                  <th className="text-left p-3 hidden md:table-cell">Methode</th>
                  <th className="text-right p-3">Betrag</th>
                </tr>
              </thead>
              <tbody>
                {(charges || []).map(c => (
                  <tr key={c.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    <td className="p-3 whitespace-nowrap">{fmtDate(c.created)}</td>
                    <td className="p-3">
                      <div className="font-medium">{c.customer_name || '-'}</div>
                      <div className="text-xs text-muted-foreground">{c.customer_email}</div>
                    </td>
                    <td className="p-3 hidden sm:table-cell font-mono text-xs">{c.invoice_number || c.event_name || '–'}</td>
                    <td className="p-3 hidden md:table-cell">
                      <span className="inline-flex items-center gap-1.5 text-xs bg-muted/50 px-2 py-0.5 border border-border">
                        <CreditCard size={12} />{c.payment_detail || c.payment_method}
                      </span>
                    </td>
                    <td className="p-3 text-right font-bold text-green-500 whitespace-nowrap">
                      <ArrowUpRight size={14} className="inline mr-1" />{fmtCHF(c.amount)}
                    </td>
                  </tr>
                ))}
                {(!charges || charges.length === 0) && (
                  <tr><td colSpan="5" className="p-8 text-center text-muted-foreground">Keine Zahlungen gefunden</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Payouts Tab */}
        {tab === 'payouts' && (
          <div className="bg-card border border-border overflow-x-auto">
            <table className="w-full text-sm" data-testid="payouts-table">
              <thead>
                <tr className="border-b border-border text-xs text-muted-foreground uppercase tracking-wider">
                  <th className="text-left p-3">Erstelldatum</th>
                  <th className="text-left p-3">Ankunft</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-right p-3">Betrag</th>
                </tr>
              </thead>
              <tbody>
                {(payouts || []).map(p => (
                  <tr key={p.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    <td className="p-3">{fmtDate(p.created)}</td>
                    <td className="p-3 font-medium">{fmtDate(p.arrival_date)}</td>
                    <td className="p-3">
                      <span className={`text-xs px-2 py-0.5 border ${p.status === 'paid' ? 'text-green-500 border-green-500/30 bg-green-500/10' : 'text-amber-500 border-amber-500/30 bg-amber-500/10'}`}>
                        {p.status === 'paid' ? 'Ausbezahlt' : p.status === 'in_transit' ? 'Unterwegs' : p.status}
                      </span>
                    </td>
                    <td className="p-3 text-right font-bold whitespace-nowrap">
                      <ArrowDownRight size={14} className="inline mr-1 text-primary" />{fmtCHF(p.amount)}
                    </td>
                  </tr>
                ))}
                {(!payouts || payouts.length === 0) && (
                  <tr><td colSpan="4" className="p-8 text-center text-muted-foreground">Keine Auszahlungen gefunden</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* All Transactions Tab */}
        {tab === 'all' && (
          <div className="bg-card border border-border overflow-x-auto">
            <table className="w-full text-sm" data-testid="transactions-table">
              <thead>
                <tr className="border-b border-border text-xs text-muted-foreground uppercase tracking-wider">
                  <th className="text-left p-3">Datum</th>
                  <th className="text-left p-3">Typ</th>
                  <th className="text-left p-3 hidden sm:table-cell">Name</th>
                  <th className="text-right p-3">Brutto</th>
                  <th className="text-right p-3 hidden md:table-cell">Gebühr</th>
                  <th className="text-right p-3">Netto</th>
                </tr>
              </thead>
              <tbody>
                {(transactions || []).map(t => {
                  const typeLabels = { charge: 'Zahlung', payout: 'Auszahlung', contribution: 'Climate', refund: 'Rückerstattung', adjustment: 'Korrektur' };
                  const isCredit = t.amount > 0;
                  return (
                    <tr key={t.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                      <td className="p-3 whitespace-nowrap">{fmtDateTime(t.created)}</td>
                      <td className="p-3">
                        <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 border ${
                          t.type === 'charge' ? 'text-green-500 border-green-500/30 bg-green-500/10' :
                          t.type === 'payout' ? 'text-blue-500 border-blue-500/30 bg-blue-500/10' :
                          t.type === 'contribution' ? 'text-emerald-500 border-emerald-500/30 bg-emerald-500/10' :
                          'text-muted-foreground border-border bg-muted/30'
                        }`}>
                          {t.type === 'contribution' && <Leaf size={10} />}
                          {typeLabels[t.type] || t.type}
                        </span>
                      </td>
                      <td className="p-3 hidden sm:table-cell">
                        {t.customer_name ? (
                          <div>
                            <div className="font-medium text-sm">{t.customer_name}</div>
                            {t.payment_method && <div className="text-xs text-muted-foreground">{t.payment_method}</div>}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-xs">-</span>
                        )}
                      </td>
                      <td className={`p-3 text-right font-medium whitespace-nowrap ${isCredit ? 'text-green-500' : 'text-foreground'}`}>
                        {fmtCHF(t.amount)}
                      </td>
                      <td className="p-3 text-right text-red-400 hidden md:table-cell whitespace-nowrap">
                        {t.fee > 0 ? `-${fmtCHF(t.fee)}` : '-'}
                      </td>
                      <td className={`p-3 text-right font-bold whitespace-nowrap ${t.net > 0 ? 'text-green-500' : t.net < 0 ? 'text-foreground' : ''}`}>
                        {fmtCHF(t.net)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Summary Footer */}
        {summary && (
          <div className="bg-card border border-border p-4 text-sm">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Brutto-Einnahmen</p>
                <p className="font-bold text-lg text-green-500">{fmtCHF(summary.total_gross)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Stripe-Gebühren</p>
                <p className="font-bold text-lg text-red-400">-{fmtCHF(summary.total_fees)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Climate</p>
                <p className="font-bold text-lg text-emerald-500">-{fmtCHF(summary.total_climate)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Netto</p>
                <p className="font-bold text-lg">{fmtCHF(summary.total_net)}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}